import React from 'react';
import { Document, Page, Text, View, StyleSheet, Image, Link, Font } from '@react-pdf/renderer';
import ReactPDFChart from 'react-pdf-charts';
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  ComposedChart,
  ErrorBar,
  Label,
  LabelList,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis
} from 'recharts';
// Register font
import LogoWithSloganGreen from '../../../assets/images/LogoWithSloganGreen.png';
import LogoWithSlogan from '../../../assets/images/LogoWithSlogan.png';
import { Farm, Field, FieldForecast, PDFFarmTotal, Variety } from 'Context/types';
import {
  calculateCentroid,
  calculatePercentageOfValues,
  getRandomHexColor,
  graphColors,
  kFormatter
} from 'Helpers/helpers';
import WeeklyWeatherOverview from './Weather/WeeklyWeatherOverview';
import PageHeader from './Weather/PageHeader';
import moment from 'moment';
import SummaryPage from './MonthlyReport/SummaryPage';
import { FarmOnboardSteps } from 'Layout/Components/CompleteRegistrationModal';
import { info } from 'console';

//--PDF INITIALIZATIONS
Font.register({
  family: 'Be Vietnam Pro',
  fonts: [
    // { src: '../Assets/BeVietnamPro-Regular.ttf', fontWeight: 'normal' },
    { src: 'fonts/BeVietnamPro-Regular.ttf', fontWeight: 'normal' },
    { src: 'fonts/BeVietnamPro-Medium.ttf', fontWeight: 'medium' },
    { src: '/fonts/BeVietnamProBold.ttf', fontWeight: 'bold' }
  ]
  // src: 'https://fonts.googleapis.com/css2?family=Be+Vietnam+Pro:ital,wght@0,100;0,200;0,300;0,400;0,500;0,600;0,700;0,800;0,900;1,100;1,200;1,300;1,400;1,500;1,600;1,700;1,800;1,900&display=swap'
});
// Create styles
const styles = StyleSheet.create({
  page: {
    flexDirection: 'row',
    fontFamily: 'Be Vietnam Pro'
  },
  section: {
    margin: 30,
    padding: 10,
    flexGrow: 1
  },
  section_title: {
    fontSize: 12,
    marginBottom: 8,
    fontFamily: 'Be Vietnam Pro'
  },
  green_page_title: {
    fontSize: 11,
    marginRight: 200
  },
  text: { margin: 12, fontSize: 14, textAlign: 'justify', fontFamily: 'Times-Roman' },
  info_card: {
    width: '180px',
    height: '80px',
    marginLeft: 12,
    borderRadius: 6,
    backgroundColor: '#00A896',
    color: '#000',
    alignItems: 'center',
    justifyContent: 'center',
    textAlign: 'left'
  },
  info_card_milestone: {
    width: '180px',
    height: '120px',
    marginLeft: 12,
    borderRadius: 6,
    color: '#000',
    alignItems: 'flex-start',
    textAlign: 'left',
    padding: 12
  },
  info_card_milestone_description: {
    fontSize: 10,
    marginTop: 8,
    fontWeight: 'normal',
    textAlign: 'left',
    color: '#000000BF'
  },
  info_card_bottom: {
    backgroundColor: '#CCEEEA',
    width: '100%',
    height: '50%',
    alignItems: 'center',
    borderRadius: 6
  },
  info_card_top: { width: '100%', height: '50%', alignItems: 'center', borderRadius: 6 },
  info_card_stems_icon: { width: '20px', height: 20, marginTop: 12 },
  info_card_stems_text: { fontSize: 12, color: '#000' },
  cover_page_link: {
    fontFamily: 'Be Vietnam Pro',
    color: '#141414',
    fontSize: 16,
    textDecoration: 'underline',
    marginTop: 6,
    marginBottom: 6
  },
  field_grid_item: {
    width: '65px',
    height: '40px',
    marginRight: 8,
    marginBottom: 8,
    paddingTop: 11,
    borderRadius: 6,
    backgroundColor: '#00A896',
    alignItems: 'center',
    color: '#ffffff',
    fontSize: 12,
    textDecoration: 'underline'
  },
  field_details: { fontSize: 12, marginBottom: 6 },
  bold_text: {
    fontWeight: 'bold'
  },
  field_weekly_forecast_container: { flexDirection: 'row', height: 34, marginBottom: 4 },
  field_weekly_forecast_left: {
    width: 50,
    backgroundColor: '#CCEEEA',
    color: '#000',
    borderTopLeftRadius: 12,
    borderBottomLeftRadius: 12,
    paddingTop: 7,
    paddingLeft: 10,
    paddingRight: 12
  },
  field_weekly_forecast_center: {
    backgroundColor: '#00A896',
    color: '#fff',
    padding: 8,
    flexDirection: 'row',
    alignItems: 'center',
    width: 95
  },
  field_weekly_forecast_right: {
    backgroundColor: '#00332d',
    color: '#fff',
    borderTopRightRadius: 12,
    borderBottomRightRadius: 12,
    padding: 8,
    flexDirection: 'row',
    alignItems: 'center',
    width: 95
  },
  daily_weather_item: { display: 'flex', flexDirection: 'row', marginLeft: 12, marginBottom: 4 },
  daily_weather_item_icon: { width: 14, marginRight: 4, padding: 2 }
});
// --- END PDF INITIALIZATIONS

const renderLabelList = (props: any) => {
  const { x, y, width, height, value, index, noKFormat } = props;

  // Custom label styles
  const customStyle = {
    fontSize: width < 50 ? 9 : 10,
    fontWeight: 'bold',
    // textAnchor: 'middle',
    fill: '#black', // white text color
    backgroundColor: '#fff', // black background
    padding: '4px 8px',
    borderRadius: '4px',
    transform: `rotate(-25 ${x} ${y})` // move label slightly above the bar
  };

  return (
    <text x={x + (width < 50 ? 0 : +(width / 2.3))} y={y + height / 1.8} style={customStyle}>
      {value > 0 ? (!noKFormat ? kFormatter(value, 0) : value) : ''}
    </text>
  );
};

// ---END GRAPH

const BarChartHorizontal = ({
  data,
  column,
  totalColumns
}: {
  data: any;
  column: 'left' | 'center' | 'right';
  totalColumns: number;
}) => {
  // Custom shape component that applies color based on variety type
  const CustomBar = (props: any) => {
    const { x, y, width, height, payload } = props;
    const barColor = payload.type?.toLowerCase() === 'spray' ? '#98C9FF' : '#4ECDC4';
    console.log('payload in custom bar', payload, barColor);

    return <rect x={x} y={y} width={width} height={height} fill={barColor} />;
  };

  // Calculate width based on number of columns
  // Total available width is approximately 750px, accounting for margins and gaps
  const chartWidth = totalColumns === 2 ? 360 : 250;

  return (
    <ReactPDFChart
      style={{ width: chartWidth, height: 420, marginRight: column !== 'right' ? 10 : 0 }}
    >
      <BarChart
        width={chartWidth}
        height={data.length * 35}
        layout="vertical"
        data={data}
        margin={{ top: 6, right: 50, left: 80, bottom: 8 }}
        barSize={15}
      >
        <XAxis type="number" domain={[0, 100]} tick={false} hide />
        <YAxis type="category" dataKey="name" tick={false} axisLine={{ strokeWidth: 0.5 }} hide />
        <Bar dataKey="accuracy" shape={<CustomBar />} isAnimationActive={false}>
          <LabelList dataKey="name" position={'left'} style={{ fontSize: 11 }} />
          <LabelList
            dataKey="accuracy"
            position="right"
            formatter={(val: number) => `${val}%`}
            style={{ fill: '#000', color: '#000' }}
          />
        </Bar>
      </BarChart>
    </ReactPDFChart>
  );
};
// ---START WEATHER FUNCTIONS
// Create Document Component
export type MonthlyPDFReportProps = {
  farm: Farm;
  varieties: Variety[];
  week_no: number;
  last_week_no: number;
  fields: Field[];
  total_farm_forecast: number;
  total_farm_harvest: number;
  total_farm_rejects: number;
  total_farm_demand: number;
  graph_data: any;
  variety_forecasts: any[];
  variety_harvests: any[];
  variety_demands: any[];
  date_string: string;
  field_forecasts: Record<string, FieldForecast>;
  farm_totals: PDFFarmTotal;
};
const MonthlyPDFReport = (props: MonthlyPDFReportProps) => (
  <Document
    key={1}
    title={`${props.farm.customer.name} -${props.farm.name} ${props.date_string} Report`}
  >
    {/* TITLE PAGE */}
    <Page size="A4" style={styles.page} orientation="landscape">
      <View style={styles.section} id="cover-page">
        <Image style={{ width: '150px' }} src={LogoWithSloganGreen}></Image>
        <Text style={{ marginTop: 154, fontSize: 14 }}>
          <Text style={{ fontWeight: 'bold', fontFamily: 'Be Vietnam Pro' }}>
            {props.farm.customer.name.toLowerCase() === props.farm.name.toLowerCase()
              ? `${props.farm.name}`
              : `${props.farm.customer.name}, ${props.farm.name}`}{' '}
            {'- '}
          </Text>
          <Text>{props.date_string}</Text>
        </Text>

        <Text style={{ marginTop: 8, marginBottom: 16, fontSize: 42, color: '#00A896' }}>
          FARM REPORT
        </Text>
      </View>

      <View
        style={{ backgroundColor: '#CCEEEA', width: 22, height: '100%', marginRight: 180 }}
      ></View>
    </Page>

    {/* OVERALL FARM REPORT PAGE */}

    <Page
      size="A4"
      orientation="landscape"
      style={{
        ...styles.page,
        backgroundColor: '#e8f3ffff'
      }}
    >
      <SummaryPage
        left_header={props.farm.name}
        left_subheader={`${props.farm.city.replaceAll('_', ' ')}, ${props.farm.country}`}
        center_header={props.date_string}
        center_subheader={`Weeks ${props.week_no} - ${props.last_week_no}`}
        week_no={props.week_no}
        styles={styles}
        total_planned={props.total_farm_demand}
        total_harvest={props.farm_totals.harvest}
        total_forecast={props.farm_totals.forecast}
        total_rejects={props.total_farm_rejects}
        graph_data={props.graph_data.farm || []}
      ></SummaryPage>
    </Page>
    {/* END OVERALL FARM REPORT PAGE */}

    {/* FOR MULTIPLE VARIETIES*/}
    {props.variety_forecasts.length > 1 && (
      <>
        {/* // START MULTI-VARIETY ACCURACY PAGE(S) */}
        {(() => {
          const allVarieties = props.graph_data.varieties.map((item: any) => ({
            name: item.variety_name.replace(/\s+/g, ''),
            accuracy: item.accuracy.toFixed(1) ?? 0,
            type: item.variety_type || 'standard'
          }));

          // Determine columns per page based on total variety count
          const totalVarieties = allVarieties.length;
          const useThreeColumns = totalVarieties > 28;
          const COLUMNS_PER_PAGE = useThreeColumns ? 3 : 2;

          // Calculate varieties per column by dividing equally
          const VARIETIES_PER_COLUMN = Math.ceil(totalVarieties / COLUMNS_PER_PAGE);
          const VARIETIES_PER_PAGE = VARIETIES_PER_COLUMN * COLUMNS_PER_PAGE;

          const totalPages = Math.ceil(allVarieties.length / VARIETIES_PER_PAGE);

          return Array.from({ length: totalPages }).map((_, pageIndex) => {
            const startIdx = pageIndex * VARIETIES_PER_PAGE;
            const endIdx = Math.min(startIdx + VARIETIES_PER_PAGE, allVarieties.length);
            const pageVarieties = allVarieties.slice(startIdx, endIdx);

            // Split varieties equally across columns
            const itemsPerColumn = Math.ceil(pageVarieties.length / COLUMNS_PER_PAGE);
            const column1 = pageVarieties.slice(0, itemsPerColumn);
            const column2 = pageVarieties.slice(itemsPerColumn, itemsPerColumn * 2);
            const column3 = useThreeColumns ? pageVarieties.slice(itemsPerColumn * 2) : [];

            return (
              <Page
                key={`accuracy-page-${pageIndex}`}
                size="A4"
                orientation="landscape"
                style={{ ...styles.page, backgroundColor: '#e8f3ffff' }}
              >
                <View
                  style={{ ...styles.section, flexDirection: 'column' }}
                  id={`overall-farm-report-${pageIndex}`}
                >
                  {/* HEADER SECTION */}
                  <View
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      marginLeft: '0%',
                      marginRight: '2%'
                    }}
                  >
                    <View
                      style={{
                        display: 'flex',
                        flexDirection: 'row',
                        justifyContent: 'space-between'
                      }}
                    >
                      <View
                        style={{
                          display: 'flex',
                          flexDirection: 'column'
                        }}
                      >
                        <Text
                          style={{
                            fontSize: '14px',
                            marginBottom: 2,
                            textAlign: 'center',
                            fontWeight: 'medium'
                          }}
                        >
                          {props.farm.name}
                        </Text>
                        <Text
                          style={{
                            fontSize: '9px',
                            marginBottom: 2,
                            textAlign: 'center',
                            fontWeight: 400
                          }}
                        >
                          {props.farm.city.replaceAll('_', ' ')} , {props.farm.country}
                        </Text>
                      </View>
                      <View style={{ alignItems: 'center' }}>
                        <Text
                          style={{
                            fontSize: '14px',
                            marginBottom: 2,
                            textAlign: 'center',
                            fontWeight: 'medium'
                          }}
                        >
                          {`${props.date_string}`}
                        </Text>
                        <Text
                          style={{
                            fontSize: '11px',
                            marginBottom: 2,
                            textAlign: 'center',
                            fontWeight: 400
                          }}
                        >
                          Weeks {props.week_no} - {props.last_week_no}
                        </Text>
                      </View>
                      <View>
                        <Image
                          style={{ width: '90px', padding: '2px' }}
                          src={LogoWithSlogan}
                        ></Image>
                      </View>
                    </View>
                  </View>

                  <Text style={{ fontSize: 12 }}>
                    Cumulative Forecast Accuracy:{' '}
                    {calculatePercentageOfValues(
                      props.farm_totals.forecast,
                      props.farm_totals.harvest
                    ).toLocaleString() + '%'}
                    {totalPages > 1 && ` (Page ${pageIndex + 1} of ${totalPages})`}
                  </Text>
                  <View
                    style={{
                      display: 'flex',
                      flexDirection: 'row',
                      flexWrap: 'nowrap',
                      // justifyContent: 'space-between',
                      marginTop: 13,
                      backgroundColor: '#ffffff',
                      borderRadius: 6,
                      paddingLeft: 5,
                      paddingRight: 5,
                      paddingTop: 6,
                      paddingBottom: 10,
                      width: '100%',
                      height: 'auto'
                    }}
                  >
                    {column1.length > 0 && (
                      <BarChartHorizontal
                        data={column1}
                        column="left"
                        totalColumns={COLUMNS_PER_PAGE}
                      />
                    )}
                    {column2.length > 0 && (
                      <BarChartHorizontal
                        data={column2}
                        column="center"
                        totalColumns={COLUMNS_PER_PAGE}
                      />
                    )}
                    {column3.length > 0 && (
                      <BarChartHorizontal
                        data={column3}
                        column="right"
                        totalColumns={COLUMNS_PER_PAGE}
                      />
                    )}
                  </View>

                  {/* Legend */}
                  <View
                    style={{
                      display: 'flex',
                      flexDirection: 'row',
                      justifyContent: 'center',
                      alignItems: 'center',
                      marginTop: 10,
                      gap: 20
                    }}
                  >
                    <View
                      style={{
                        display: 'flex',
                        flexDirection: 'row',
                        alignItems: 'center',
                        gap: 6
                      }}
                    >
                      <View
                        style={{
                          width: 12,
                          height: 12,
                          borderRadius: 6,
                          backgroundColor: '#98C9FF'
                        }}
                      />
                      <Text style={{ fontSize: 10 }}>Spray</Text>
                    </View>
                    <View
                      style={{
                        display: 'flex',
                        flexDirection: 'row',
                        alignItems: 'center',
                        gap: 6
                      }}
                    >
                      <View
                        style={{
                          width: 12,
                          height: 12,
                          borderRadius: 6,
                          backgroundColor: '#4ECDC4'
                        }}
                      />
                      <Text style={{ fontSize: 10 }}>Standard</Text>
                    </View>
                  </View>
                </View>
              </Page>
            );
          });
        })()}
        {/* // END MULTI-VARIETY ACCURACY PAGE(S) 
        // START VARIETY SUMMARY PAGE */}
        {props.variety_forecasts.map((variety_forecast: any, var_index: number) => (
          <Page
            key={var_index}
            size="A4"
            orientation="landscape"
            style={{
              ...styles.page,
              backgroundColor: '#e8f3ffff'
            }}
          >
            <SummaryPage
              left_header={variety_forecast.variety_name}
              left_subheader={`${props.farm.name} ${props.farm.city.replaceAll('_', ' ')}, ${
                props.farm.country
              }`}
              center_header={props.date_string}
              center_subheader={`Weeks ${props.week_no} - ${props.last_week_no}`}
              week_no={props.week_no}
              styles={styles}
              total_planned={props.variety_demands[var_index]
                .slice(props.week_no - 1, props.last_week_no)
                .reduce((acc: any, curr: any) => (acc.stems ?? 0) + (curr.stems ?? 0), 0)}
              total_forecast={variety_forecast?.forecasts
                .slice(props.week_no - 1, props.last_week_no)
                .reduce((acc: number, curr: number) => acc + curr, 0)}
              total_harvest={props.variety_harvests[var_index]?.harvests
                .slice(props.week_no - 1, props.last_week_no)
                .reduce((acc: number, curr: number) => acc + curr, 0)}
              total_rejects={props.variety_harvests[var_index]?.rejects
                .slice(props.week_no - 1, props.last_week_no)
                .reduce((acc: number, curr: number) => acc + curr, 0)}
              graph_data={props.graph_data.varieties[var_index].data || []}
            ></SummaryPage>
          </Page>
        ))}
      </>
    )}

    {/* END VARIETY SUMMARY PAGE */}
    {/* START MILESTONE OVERVIEW PAGE */}
    {props.farm.customer.id == 3 && ( // hardcoded for only AAA farm report
      <Page
        size="A4"
        orientation="landscape"
        style={{
          ...styles.page,
          backgroundColor: '#e8f3ffff'
        }}
      >
        <View style={styles.section} id="milestone-overview">
          {/* headers */}
          <View
            style={{
              display: 'flex',
              flexDirection: 'column',
              marginLeft: '0%',
              marginRight: '2%'
            }}
          >
            <View
              style={{
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'space-between'
              }}
            >
              <View
                style={{
                  display: 'flex',
                  flexDirection: 'column'
                }}
              >
                <Text
                  style={{
                    fontSize: '14px',
                    marginBottom: 2,
                    textAlign: 'center',
                    fontWeight: 'medium'
                  }}
                >
                  {props.farm.name} {props.varieties[0].name}
                </Text>
                <Text
                  style={{
                    fontSize: '9px',
                    marginBottom: 2,
                    textAlign: 'center',
                    fontWeight: 400
                  }}
                >
                  {props.farm.city.replaceAll('_', ' ')} , {props.farm.country}
                </Text>
              </View>
              <View style={{ alignItems: 'center' }}>
                <Text
                  style={{
                    fontSize: '14px',
                    marginBottom: 2,
                    textAlign: 'center',
                    fontWeight: 'medium'
                  }}
                >
                  {`Production & Forecast`}
                </Text>
                <Text
                  style={{
                    fontSize: '11px',
                    marginBottom: 2,
                    textAlign: 'center',
                    fontWeight: 400
                  }}
                >
                  {`Weeks ${props.week_no} - ${props.last_week_no}`}
                </Text>
              </View>
              <View>
                <Image style={{ width: '90px', padding: '2px' }} src={LogoWithSlogan}></Image>
              </View>
            </View>
          </View>

          {/* summary cards section */}
          <View style={{ flexDirection: 'row', marginTop: 24 }}>
            <View
              style={{
                ...styles.info_card_milestone,
                backgroundColor: '#5C5C5CB2'
              }}
            >
              <Text
                style={{ fontSize: 16, fontWeight: 'medium', marginBottom: 4, textAlign: 'left' }}
              >
                {props.farm_totals.harvest.toLocaleString()}
                {/* {' \u2022'} <Text style={{ fontSize: 12 }}>{'41.68%'}</Text> */}
              </Text>
              <Text style={{ fontSize: 12, textAlign: 'left' }}>Packhouse Intake</Text>
              <Text style={styles.info_card_milestone_description}>
                {`Stems received at ${props.farm.name} packhouse`}
              </Text>
            </View>
            {/* latest forecast */}
            <View
              style={{
                ...styles.info_card_milestone,
                backgroundColor: '#98C9FF'
              }}
            >
              <Text style={{ fontSize: 16, fontWeight: 'medium', marginBottom: 4 }}>
                {props.farm_totals.forecast.toLocaleString()}
                {' \u2022'}{' '}
                <Text style={{ fontSize: 12 }}>
                  {calculatePercentageOfValues(
                    props.farm_totals.forecast,
                    props.farm_totals.harvest
                  ).toLocaleString() + '%'}
                </Text>
              </Text>
              <Text style={{ fontSize: 12 }}>Lima Latest Forecast</Text>
              <Text style={styles.info_card_milestone_description}>
                {props.farm_totals.forecast >= props.farm_totals.harvest
                  ? `Lima over-forecasted by ${(
                      props.farm_totals.forecast - props.farm_totals.harvest
                    ).toLocaleString()}`
                  : `Lima under-forecasted by ${(
                      props.farm_totals.harvest - props.farm_totals.forecast
                    ).toLocaleString()}`}
              </Text>
            </View>
            {/* 1 week out forecast */}
            <View
              style={{
                ...styles.info_card_milestone,
                backgroundColor: '#87C0FF'
              }}
            >
              <Text style={{ fontSize: 16, fontWeight: 'medium', marginBottom: 4 }}>
                {props.farm_totals.weeks_out_1.toLocaleString()}
                {' \u2022'}{' '}
                <Text style={{ fontSize: 12 }}>
                  {calculatePercentageOfValues(
                    props.farm_totals.weeks_out_1,
                    props.farm_totals.harvest
                  ).toLocaleString() + '%'}
                </Text>
              </Text>
              <Text style={{ fontSize: 12 }}>Lima 1 Week Out</Text>
              <Text style={styles.info_card_milestone_description}>
                {props.farm_totals.weeks_out_1 >= props.farm_totals.harvest
                  ? `Lima over-forecasted by ${(
                      props.farm_totals.weeks_out_1 - props.farm_totals.harvest
                    ).toLocaleString()}`
                  : `Lima under-forecasted by ${(
                      props.farm_totals.harvest - props.farm_totals.weeks_out_1
                    ).toLocaleString()}`}
              </Text>
            </View>
            {/* 2 weeks out forecast */}
            <View
              style={{
                ...styles.info_card_milestone,
                backgroundColor: '#BADBFF'
              }}
            >
              <Text style={{ fontSize: 16, fontWeight: 'medium', marginBottom: 4 }}>
                {props.farm_totals.weeks_out_2.toLocaleString()}
                {' \u2022'}{' '}
                <Text style={{ fontSize: 12 }}>
                  {calculatePercentageOfValues(
                    props.farm_totals.weeks_out_2,
                    props.farm_totals.harvest
                  ).toLocaleString() + '%'}
                </Text>
              </Text>
              <Text style={{ fontSize: 12 }}>Lima 2 Weeks Out</Text>
              <Text style={styles.info_card_milestone_description}>
                {props.farm_totals.weeks_out_2 >= props.farm_totals.harvest
                  ? `Lima over-forecasted by ${(
                      props.farm_totals.weeks_out_2 - props.farm_totals.harvest
                    ).toLocaleString()}`
                  : `Lima under-forecasted by ${(
                      props.farm_totals.harvest - props.farm_totals.weeks_out_2
                    ).toLocaleString()}`}
              </Text>
            </View>
          </View>

          {/* milestone graph section */}

          <View
            style={{
              marginTop: 40,
              marginLeft: 10,
              left: 0,
              paddingTop: 30,
              paddingBottom: 20,
              paddingRight: 20,
              paddingLeft: 20,
              backgroundColor: '#ffffff',
              borderRadius: 6
            }}
          >
            <ReactPDFChart style={{ left: -30 }}>
              <ComposedChart
                width={750}
                height={250}
                data={props.graph_data.milestone || []}
                style={{ left: 0 }}
                barGap={2}
                barSize={18}
                margin={{ top: 30 }}
              >
                <CartesianGrid strokeDasharray="0" vertical={false} />
                <XAxis dataKey="name" interval={0} tick={{ fontSize: 9 }} />
                <YAxis
                  tickCount={6}
                  tickFormatter={(value: string) => value && kFormatter(Number(value), 1)}
                  tick={{ fontSize: 9 }}
                />
                <Legend iconType="circle" iconSize={10} width={830} align="left" />
                <Bar
                  dataKey={'Actuals'}
                  fill={'#626262'}
                  isAnimationActive={false}
                  radius={[3, 3, 0, 0]}
                ></Bar>
                <Bar
                  dataKey={'Forecasts'}
                  fill={'#53A5FF'}
                  isAnimationActive={false}
                  radius={[3, 3, 0, 0]}
                ></Bar>
                <Bar
                  dataKey={'weeks_out_1'}
                  fill={'#87C0FF'}
                  isAnimationActive={false}
                  name={'1 Week Out'}
                  radius={[3, 3, 0, 0]}
                ></Bar>
                <Bar
                  dataKey={'weeks_out_2'}
                  fill={'#BADBFF'}
                  isAnimationActive={false}
                  name={'2 Weeks Out'}
                  radius={[3, 3, 0, 0]}
                ></Bar>
              </ComposedChart>
            </ReactPDFChart>
          </View>
        </View>
      </Page>
    )}
  </Document>
);

export default MonthlyPDFReport;
